import { DateTime } from 'luxon';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import { TypeUtils } from '../../common/misc';
import { Regex } from '.';

const DATE_STRING_FORMAT = ['yyyy-MM-dd', '\'T\'', 'HH:mm:ss', '\'Z\''].join('');
const ISO_EXPLICIT_OFFSET_REGEX = /(?:Z|[+-]\d{2}:?\d{2})$/i;

type ConvertDateValueToStringOptions = {
    applyStartOfDay?: boolean;
    applyEndOfDay?: boolean;
};

export default class Utils {
    static convertDateValueToPickerValue(value: unknown): unknown {
        if (TypeUtils.hasLegacyMomentShape(value)) {
            return value;
        }

        const dateTime = Utils.convertDateValueToDateTime(value);

        if (!dateTime) {
            return undefined;
        }

        return momentGenerateConfig.getFixedDate(dateTime.toFormat('yyyy-MM-dd'));
    }

    static convertDateValueToDate(value: unknown): Date | null {
        const dateTime = Utils.convertDateValueToDateTime(value);
        return dateTime ? dateTime.toJSDate() : null;
    }

    static formatAbsoluteDateValue(value: unknown): string | null {
        if (typeof value === 'string') {
            const parsedIsoDate = DateTime.fromISO(value, { setZone: true });
            return parsedIsoDate.isValid ? parsedIsoDate.toFormat('yyyy-MM-dd') : null;
        }

        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            const parsedJsDate = DateTime.fromJSDate(value);
            return parsedJsDate.isValid ? parsedJsDate.toFormat('yyyy-MM-dd') : null;
        }

        return null;
    }

    static convertDateValueToString(value: unknown, options: ConvertDateValueToStringOptions = {}): string | null {
        const formatDateTime = (valueDateTime: DateTime) => {
            let normalizedDateTime = valueDateTime;

            if (options.applyStartOfDay) {
                normalizedDateTime = DateTime.utc(valueDateTime.year, valueDateTime.month, valueDateTime.day).startOf(
                    'day'
                );
            }

            if (options.applyEndOfDay) {
                normalizedDateTime = DateTime.utc(valueDateTime.year, valueDateTime.month, valueDateTime.day).endOf(
                    'day'
                );
            }

            return Utils.converMomentToDateString(normalizedDateTime);
        };

        const parsedDateTime = Utils.convertDateValueToDateTime(value);

        if (parsedDateTime) {
            return formatDateTime(parsedDateTime);
        }

        return null;
    }

    static converMomentToDateString(value: unknown): string {
        const dateTime = Utils.convertDateValueToDateTime(value);

        if (!dateTime) {
            return '';
        }

        return dateTime.toUTC().toFormat(DATE_STRING_FORMAT);
    }

    static convertToStringArray(value: unknown): string[] {
        if (!value) {
            return [];
        }

        if (TypeUtils.isStringArray(value)) {
            return value;
        }

        if (typeof value === 'string') {
            return [value];
        }

        return [];
    }

    static hasValue(value: unknown): boolean {
        if (value === null || value === undefined) {
            return false;
        }

        if (TypeUtils.isString(value)) {
            return value.length > 0;
        }

        if (TypeUtils.isNumber(value) || TypeUtils.isBoolean(value)) {
            return true;
        }

        if (TypeUtils.isArray(value)) {
            return value.length > 0;
        }

        if (TypeUtils.isMoment(value) || TypeUtils.isDate(value)) {
            return true;
        }

        return TypeUtils.isObject(value);
    }

    static groupArray<T>(inputArray: T[], groupSize: number = 4) {
        return inputArray.reduce((acc: T[][], currentValue: T, currentIndex: number) => {
            const groupIndex = Math.floor(currentIndex / groupSize);

            if (!acc[groupIndex]) {
                acc[groupIndex] = [];
            }

            acc[groupIndex].push(currentValue);

            return acc;
        }, []);
    }

    // Export types are missing for react-maskinput lib
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static reformatMaskedInput(params: any) {
        const regex = Regex.decimalRegex;
        let value = params.visibleValue ?? params.value;

        const resetValue = () => {
            params.value = params.visibleValue = '';
            params.selection.start = params.visibleValue.length;
            params.selection.end = params.visibleValue.length;
            return params;
        };

        if (!TypeUtils.isString(value)) {
            return resetValue();
        }

        value = value.replace(regex, ',') as string;
        const inputValue = (params.input ?? '') as string;

        params.visibleValue = (
            value.substring(0, params.selection.start) +
            inputValue +
            value.substring(params.selection.end)
        ).replace(regex, ',');

        if (params.visibleValue.trim().length === 0) {
            return resetValue();
        }

        params.selection.start = params.visibleValue.length;
        params.selection.end = params.visibleValue.length;

        return params;
    }

    static createNumberArray(start: number, end: number) {
        if (start > end) {
            throw new Error('Start should be less than or equal to end');
        }

        return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    }

    static formatAmountWithTwoDecimals(value: number): string {
        return Utils.formatAmount(parseFloat(value.toFixed(2)));
    }

    static formatAmount(value: unknown) {
        const formatString = (str: string) => str.replace(Regex.decimalRegex, ',');

        if (TypeUtils.isString(value)) {
            return formatString(value);
        }

        if (TypeUtils.isNumber(value)) {
            return formatString(value.toString());
        }

        return '';
    }

    static filterEmptyEntries<T extends Record<string, unknown>>(obj: T) {
        return Object.fromEntries(Object.entries(obj).filter(entry => entry[1] !== undefined));
    }

    static serializeForComparison(value: unknown): string {
        if (TypeUtils.isMoment(value)) {
            return Utils.converMomentToDateString(value);
        }

        return JSON.stringify(value);
    }

    private static convertDateValueToDateTime(value: unknown): DateTime | null {
        if (DateTime.isDateTime(value)) {
            return value;
        }

        if (TypeUtils.hasLegacyMomentShape(value)) {
            if (value instanceof Date) {
                const fromDate = DateTime.fromJSDate(value);
                return fromDate.isValid ? fromDate : null;
            }

            if (value && typeof value === 'object' && 'valueOf' in value && typeof value.valueOf === 'function') {
                const valueOf = value.valueOf();

                if (typeof valueOf === 'number' && Number.isFinite(valueOf)) {
                    const fromMillis = DateTime.fromMillis(valueOf);
                    return fromMillis.isValid ? fromMillis : null;
                }
            }
        }

        if (TypeUtils.isString(value)) {
            const fromIso = DateTime.fromISO(value, { setZone: true });
            if (fromIso.isValid) {
                return ISO_EXPLICIT_OFFSET_REGEX.test(value.trim()) ? fromIso.toUTC() : fromIso;
            }

            const fromDate = DateTime.fromJSDate(new Date(value));
            return fromDate.isValid ? fromDate : null;
        }

        if (TypeUtils.isDate(value)) {
            const fromDate = DateTime.fromJSDate(new Date(value));
            return fromDate.isValid ? fromDate : null;
        }

        if (TypeUtils.isNumber(value)) {
            const fromMillis = DateTime.fromMillis(value);
            return fromMillis.isValid ? fromMillis : null;
        }

        return null;
    }
}
